import * as moment from 'moment';
import {AssetModel} from '../_models/asset.model';
import {AssetsService} from './assets.service';
import {BehaviorSubject, forkJoin, Observable, of, Subject} from 'rxjs';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {ErrorService} from '../../shared/_services/error.service';
import {HttpClient} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {MercureDataModel, MercureModel} from '../_models/mercure.model';
import {DfRenameInput, DfService} from '../../shared/_services/df.service';
import {DateService} from './date.service';
import {SbService} from '../../shared/_services/sb.service';
import {SoService} from '../../shared/_services/so.service';
import {DATE_FORMAT, DateModel} from '../_models/date.model';

@Injectable({
  providedIn: 'root'
})
export class MercureService implements OnDestroy {

  private mercure$ = new BehaviorSubject<MercureModel[]>([]);

  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private assetsService: AssetsService,
    private dateService: DateService,
    private errorService: ErrorService,
    private httpClient: HttpClient,
    private sbService: SbService,
    private soService: SoService,
  ) {
    this.getSo();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  // ********************************************************************************************************
  // LOAD DATA
  // ********************************************************************************************************

  private getSo(): void {
    this.soService.onNewSoActive()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (so) => {
          if (so === '') {
            this.callGetMercure();
          }
        }
      );
  }

  private callGetMercure(): void {
    const assets = this.assetsService.getAssets();
    const curDate = this.dateService.getNominationDate();
    if (assets?.length && curDate) {
      this.geMercureHttp(assets, curDate)
        .subscribe(
          (data) => {
            const countries: MercureModel[] = [];
            for (const country of data) {
              const dfInput: DfRenameInput[] = [];
              for (const series of country) {
                dfInput.push({
                  selColumns: ['timestamp', 'value'],
                  renameColumns: ['timestamp', series.mercureId],
                  dfData: series.mercureData,
                });
              }
              const merged = DfService.dfMergeRename(dfInput, 'outer', 'timestamp');
              countries.push({country: country[0].country, mercureData: merged.map((m) => new MercureDataModel(m))});
            }
            this.newMercure(countries);
          },
          (err) => {
            console.log(err);
            this.newMercure([]);
            this.sbService.message('Something went wrong loading the market forecast data: ' + err.errMessage);
          }
        );
    }
  }

  private geMercureHttp(assets: AssetModel[], date: DateModel): Observable<any> {
    const httpRequests = [];
    for (const country of assets) {
      httpRequests.push(
        forkJoin([
          this.buildHttpRequest(date, country.label, 'SOLAR_FORECAST'),
          this.buildHttpRequest(date, country.label, 'WIND_FORECAST'),
          this.buildHttpRequest(date, country.label, 'RESLOAD_FORECAST'),
        ])
      );
    }
    if (!httpRequests.length) {
      return of([]);
    }
    return forkJoin(httpRequests);
  }

  private buildHttpRequest(date: DateModel, country: string, id: string): Observable<any> {
    return this.httpClient.get(
      environment.urlWarp + '/read/mercure/' + country + '_' + id +
      '?start=' + date.date +
      '&stop=' + date.date +
      '&start=' + moment(date.date).add(date.horAsOf, 'day').format(DATE_FORMAT) +
      '&stop=' + moment(date.date).add(date.horUpTo, 'day').format(DATE_FORMAT)
    ).pipe(
      map((r: any) => {
        return {
          country: country,
          mercureId: id,
          mercureData: r.data || []
        };
      }),
      catchError(this.errorService.handleError)
    );
  }

  // ********************************************************************************************************
  // PROCESS DATA
  // ********************************************************************************************************

  // ********************************************************************************************************
  // BROADCAST DATA
  // ********************************************************************************************************

  newMercure(data: MercureModel[]) {
    this.mercure$.next(data);
  }

  onNewMercure(): Observable<MercureModel[]> {
    return this.mercure$.asObservable();
  }

  getMercure(): MercureModel[] {
    return this.mercure$.getValue();
  }

}
